import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";

/**
 * Foraging — open-arena collect, redesigned for 3D depth + engagement.
 * Game 05 of 12. (v2 — feel-pass sweep.)
 *
 * Scoring contract preserved: seed-driven orb schedule + pickup events
 * (t, orb_idx) replay unchanged. Visual layer rebuilt: 3D spheres,
 * pitched camera, real lighting, biome cycling, chain-streak halo.
 */

// v4 — bigger world (mirrors scoring-foraging server bump).
export const ARENA_HALF = 40;
export const ORB_LIFE_MS = 4000;
export const RUN_MS = 90_000;
export const PICKUP_RADIUS = 1.2;
const PREDATOR_EVERY_CLIENT = 10;
/** v5(a) — must mirror scoring-foraging PREDATOR_SPEED exactly so the
 *  client pickup detection lands on the same predator position the
 *  validator will replay. */
const PREDATOR_SPEED_CLIENT = 4;
/** Magnet-active pickup radius — applied while a brew is active. */
export const MAGNET_RADIUS = 2.6;
/** Duration of the magnet effect after a brew triggers. */
export const MAGNET_DURATION_MS = 5000;
/** Bonus +500 mirrored from scoring-foraging.ts BREW_BONUS. */
export const BREW_BONUS_CLIENT = 500;
export const MAX_SHIP_SPEED = 25;
const DENSITY_START = 1;
const DENSITY_END = 4;

type OrbColor = 0 | 1 | 2 | 3;

export interface Pickup { t: number; orb_idx: number }

export interface ForagingCallbacks {
  onHud: (state: {
    runMs: number;
    pickups: number;
    scoreEstimate: number;
    chain: number;
    brews: number;
    magnetTimeLeftMs: number;
  }) => void;
  onGameOver: (result: { pickups: Pickup[]; scoreEstimate: number; bestChain: number; pickupCount: number; brews: number; }) => void;
}

interface Orb { idx: number; spawn_t_ms: number; x: number; y: number; color: OrbColor; dir_x?: number; dir_y?: number; group?: THREE.Group; mat?: THREE.MeshStandardMaterial; ringMat?: THREE.MeshBasicMaterial; picked: boolean; spawned: boolean; }

interface BiomePalette { floor: number; rim: number; fog: number; }
const PALETTES: BiomePalette[] = [
  { floor: 0x040810, rim: 0x46ffe0, fog: 0x050a14 },
  { floor: 0x0a0518, rim: 0xff3df0, fog: 0x100828 },
  { floor: 0x100c05, rim: 0xffd54a, fog: 0x1a1408 },
];

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function smoothstep(t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return c * c * (3 - 2 * c);
}
/** v5(a) — predator drift angle. Mirrors scoring-foraging exactly so
 *  the client and server share the predator's path. Independent of the
 *  orb-generation rng stream. */
function predatorAngle(seed: number, idx: number): number {
  let s = ((seed ^ Math.imul(idx, 0x9e3779b1)) | 0) + 0x6d2b79f5;
  let t = s;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  const u = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return u * Math.PI * 2;
}
function predatorPosAt(o: Orb, runMs: number): { x: number; y: number } {
  if (o.color !== 3 || o.dir_x === undefined || o.dir_y === undefined) {
    return { x: o.x, y: o.y };
  }
  const age = Math.max(0, runMs - o.spawn_t_ms);
  const ageS = age / 1000;
  const px = o.x + o.dir_x * PREDATOR_SPEED_CLIENT * ageS;
  const py = o.y + o.dir_y * PREDATOR_SPEED_CLIENT * ageS;
  return {
    x: Math.max(-ARENA_HALF, Math.min(ARENA_HALF, px)),
    y: Math.max(-ARENA_HALF, Math.min(ARENA_HALF, py)),
  };
}
export function generateOrbs(seed: number): Orb[] {
  const rng = makeRng(seed);
  const out: Orb[] = [];
  let t = 500;
  let idx = 0;
  while (t < RUN_MS) {
    const u = t / RUN_MS;
    const density = DENSITY_START + (DENSITY_END - DENSITY_START) * smoothstep(u);
    const dt = 1000 / density;
    const x = (rng() - 0.5) * 2 * ARENA_HALF;
    const y = (rng() - 0.5) * 2 * ARENA_HALF;
    // v4 — mirror server: every PREDATOR_EVERY_CLIENT-th orb is a predator (3),
    // others use the rng-derived 0|1|2 base color. The rng() roll is consumed
    // either way so the schedule stays aligned with the server.
    const baseColor = Math.floor(rng() * 3) as 0 | 1 | 2;
    const isPredator = idx > 0 && idx % PREDATOR_EVERY_CLIENT === 0;
    const color: OrbColor = isPredator ? 3 : baseColor;
    if (isPredator) {
      const angle = predatorAngle(seed, idx);
      out.push({
        idx, spawn_t_ms: Math.round(t), x, y, color,
        dir_x: Math.cos(angle), dir_y: Math.sin(angle),
        picked: false, spawned: false,
      });
    } else {
      out.push({ idx, spawn_t_ms: Math.round(t), x, y, color, picked: false, spawned: false });
    }
    t += dt;
    idx += 1;
  }
  return out;
}

const COLOR_HEX = [0x00e0ff, 0xff3df0, 0xffd54a, 0xff2a3a];
const COLOR_EMISSIVE = [0x0080cc, 0xcc00aa, 0xff8800, 0xcc0011];

export class Foraging {
  private engine: Engine;
  private audio = new AudioBed();
  private orbs: Orb[];
  private ship: THREE.Group;
  private shipHalo: THREE.Mesh;
  private shipHaloMat: THREE.MeshBasicMaterial;
  private shipPos = new THREE.Vector2();
  private pointerPos = new THREE.Vector2();
  private pointerActive = false;
  private startedAt = 0;
  private gameOver = false;
  private pickups: Pickup[] = [];
  private scoreEstimate = 0;
  private chain = 0;
  private bestChain = 0;
  private lastColor: OrbColor | -1 = -1;
  // v3 brew state
  private brews = 0;
  private magnetUntil = 0;
  private brewFx: THREE.Mesh | null = null;
  private brewFxBornMs = 0;
  /** v5(b) — brew aftermath fog tint. While `brewFogTintUntil > runMs`,
   *  the scene fog blends toward `brewFogColor` proportional to time
   *  remaining. ~2 s arc lets the color shift register without
   *  competing with the biome cycle. */
  private brewFogTintUntil = 0;
  private brewFogColor = new THREE.Color(0xffffff);
  private brewFogTmp = new THREE.Color();
  private cb: ForagingCallbacks;
  private canvas: HTMLCanvasElement;
  private floor: THREE.Mesh;
  private floorMat: THREE.MeshStandardMaterial;
  private rim: THREE.LineSegments;
  private rimMat: THREE.LineBasicMaterial;
  private currentBiome = 0;
  private targetBiome = 0;
  private biomeBlend = 0;
  /** v5(c) — routing hint mode. When prefers-reduced-motion is on, the
   *  visual decay arc fades more aggressively so the player can read
   *  remaining value from the orb's brightness/scale without relying on
   *  motion cues. Scoring + pickup radius unchanged. */
  private readonly reducedMotion: boolean =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  private pickupRings: { mesh: THREE.Mesh; born: number }[] = [];
  private pickupRingMat: THREE.MeshBasicMaterial;
  private pickupRingGeo: THREE.RingGeometry;
  // v6 — parallax starfield layers (3) behind the arena so the wide
  // top-down view reads as a real 3D world with horizon depth, not a
  // 2D HTML5 board. Per memory feedback-sibling-games-shallow.
  private starLayers: THREE.Points[] = [];
  private seed: number;

  private onPointerDown = (e: PointerEvent) => { this.pointerActive = true; this.updatePointer(e); };
  private onPointerMove = (e: PointerEvent) => { if (this.pointerActive) this.updatePointer(e); };
  private onPointerUp = () => { this.pointerActive = false; };

  constructor(canvas: HTMLCanvasElement, cb: ForagingCallbacks, seed = Math.floor(Math.random() * 0x7fffffff)) {
    this.canvas = canvas;
    this.cb = cb;
    this.seed = seed;
    this.orbs = generateOrbs(seed);

    this.engine = new Engine(canvas, { onFrame: (dt, t) => this.frame(dt, t) });
    // Pitched camera — looking down at the arena from one corner.
    this.engine.camera.position.set(0, -22, 38);
    this.engine.camera.lookAt(0, 8, 0);
    this.engine.camera.fov = 58;
    this.engine.camera.updateProjectionMatrix();
    this.engine.scene.fog = new THREE.Fog(PALETTES[0].fog, 32, 90);

    const ambient = new THREE.AmbientLight(0x6080a0, 0.45);
    this.engine.scene.add(ambient);
    const key = new THREE.DirectionalLight(0xfff0d0, 1.0);
    key.position.set(8, 12, 16);
    this.engine.scene.add(key);
    const fill = new THREE.DirectionalLight(0x4060ff, 0.45);
    fill.position.set(-8, 6, 8);
    this.engine.scene.add(fill);

    // Floor — wider than the arena so the rim sits inside the view.
    const floorGeo = new THREE.PlaneGeometry(ARENA_HALF * 4, ARENA_HALF * 4);
    this.floorMat = new THREE.MeshStandardMaterial({ color: PALETTES[0].floor, roughness: 0.92, metalness: 0.1 });
    this.floor = new THREE.Mesh(floorGeo, this.floorMat);
    this.floor.rotation.x = -Math.PI / 2;
    this.floor.position.y = -0.5;
    this.engine.scene.add(this.floor);

    // Grid lines drawn on the floor.
    const gridPts: number[] = [];
    for (let i = -ARENA_HALF; i <= ARENA_HALF; i += 5) {
      gridPts.push(i, -0.49, -ARENA_HALF, i, -0.49, ARENA_HALF);
      gridPts.push(-ARENA_HALF, -0.49, i, ARENA_HALF, -0.49, i);
    }
    const gridGeo = new THREE.BufferGeometry();
    gridGeo.setAttribute("position", new THREE.Float32BufferAttribute(gridPts, 3));
    this.rimMat = new THREE.LineBasicMaterial({ color: PALETTES[0].rim, transparent: true, opacity: 0.22, blending: THREE.AdditiveBlending });
    this.rim = new THREE.LineSegments(gridGeo, this.rimMat);
    this.engine.scene.add(this.rim);

    // Ship — 3D wedge + halo.
    const shipBodyGeo = new THREE.ConeGeometry(0.45, 1.2, 16);
    const shipBodyMat = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0x66ddff, emissiveIntensity: 1.5, roughness: 0.25, metalness: 0.6 });
    const shipBody = new THREE.Mesh(shipBodyGeo, shipBodyMat);
    shipBody.rotation.x = -Math.PI / 2;
    this.ship = new THREE.Group();
    this.ship.add(shipBody);
    const shipHaloGeo = new THREE.RingGeometry(0.7, 1.1, 32);
    this.shipHaloMat = new THREE.MeshBasicMaterial({ color: 0x66ddff, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.shipHalo = new THREE.Mesh(shipHaloGeo, this.shipHaloMat);
    this.shipHalo.rotation.x = -Math.PI / 2;
    this.shipHalo.position.y = -0.4;
    this.ship.add(this.shipHalo);
    this.engine.scene.add(this.ship);

    // Pickup-ring effect pool (shared geo/mat).
    this.pickupRingGeo = new THREE.RingGeometry(0.6, 0.9, 32);
    this.pickupRingMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });

    // v6 — parallax starfield. Three THREE.Points layers BEHIND the
    // arena (large -z), with y biased upward so most stars sit above
    // the opaque floor in the camera's tilted forward view. Seed-
    // derived positions give a reproducible starscape per run. fog=false
    // so far stars persist past the FogExp2 falloff (32→90 units).
    // Per memory feedback-sibling-games-shallow §"real 3D scene".
    const makeStars = (count: number, z: number, radius: number, opts: { size: number; color: number }) => {
      const positions = new Float32Array(count * 3);
      let s = (this.seed ^ Math.imul(z | 0, 0x9e3779b1)) | 0;
      const r = () => {
        s = (s + 0x6d2b79f5) | 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (r() - 0.5) * radius * 2;
        // Bias y upward — the floor occludes negative-y stars, the
        // camera looks up-and-forward toward (0, 8, 0) so the visible
        // band is roughly y ∈ [+30, +large] at z=-200. Range here is
        // [-0.1*radius, +0.9*radius] which sits mostly above floor.
        positions[i * 3 + 1] = (r() - 0.1) * radius;
        positions[i * 3 + 2] = z + (r() - 0.5) * 8;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        color: opts.color, size: opts.size, sizeAttenuation: true,
        transparent: true, opacity: 0.85, depthWrite: false, fog: false,
      });
      const pts = new THREE.Points(geo, mat);
      this.engine.scene.add(pts);
      this.starLayers.push(pts);
    };
    makeStars(550, -200, 220, { size: 0.6,  color: 0x8a92b8 }); // far — cool blue/gray
    makeStars(280, -110, 120, { size: 0.5,  color: 0xaab0d4 }); // mid — neutral
    makeStars(110,  -55,  70, { size: 0.45, color: 0xffe0c0 }); // near — warm accent

    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
  }

  start() {
    this.audio.arm();
    // v6 — baseline floor for the adaptive drone. frame() ramps this up
    // with chain engagement (0.15 + chain/5 * 0.55). 0.4 used to be a
    // fixed seed here; that hid the audio escalation hook.
    this.audio.setDroneIntensity(0.15);
    this.startedAt = performance.now();
    this.gameOver = false;
    this.shipPos.set(0, 0);
    this.pickups = [];
    this.scoreEstimate = 0;
    this.chain = 0;
    this.bestChain = 0;
    this.brews = 0;
    this.magnetUntil = 0;
    this.brewFogTintUntil = 0;
    if (this.brewFx) {
      this.engine.scene.remove(this.brewFx);
      this.brewFx.geometry.dispose();
      (this.brewFx.material as THREE.Material).dispose();
      this.brewFx = null;
    }
    this.lastColor = -1;
    this.currentBiome = 0;
    this.targetBiome = 0;
    this.biomeBlend = 0;
    for (const o of this.orbs) { o.picked = false; o.spawned = false; }
    this.engine.start();
  }

  private updatePointer(e: PointerEvent) {
    const rect = this.canvas.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    // Project onto y=0 plane (the game plane) through the pitched camera.
    const ndc = new THREE.Vector3(nx, ny, 0.5).unproject(this.engine.camera);
    const dir = ndc.sub(this.engine.camera.position).normalize();
    const dist = -this.engine.camera.position.y / dir.y;
    const wx = this.engine.camera.position.x + dir.x * dist;
    const wz = this.engine.camera.position.z + dir.z * dist;
    this.pointerPos.set(wx, wz);
  }

  private spawnOrb(o: Orb) {
    if (o.spawned) return;
    o.spawned = true;
    const orbGeo = new THREE.SphereGeometry(0.55, 18, 14);
    const orbMat = new THREE.MeshStandardMaterial({
      color: COLOR_HEX[o.color],
      emissive: COLOR_EMISSIVE[o.color],
      emissiveIntensity: 1.4,
      roughness: 0.3,
      metalness: 0.5,
    });
    const body = new THREE.Mesh(orbGeo, orbMat);

    // Floor halo under each orb (where it sits on the grid).
    const haloGeo = new THREE.RingGeometry(0.7, 1.0, 24);
    const haloMat = new THREE.MeshBasicMaterial({ color: COLOR_HEX[o.color], transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    const halo = new THREE.Mesh(haloGeo, haloMat);
    halo.rotation.x = -Math.PI / 2;
    halo.position.y = -0.4;

    const group = new THREE.Group();
    group.add(body);
    group.add(halo);
    // Place orbs into XZ plane (camera looks down y).
    group.position.set(o.x, 0.6, o.y);
    o.group = group;
    o.mat = orbMat;
    o.ringMat = haloMat;
    this.engine.scene.add(group);
  }

  private despawnOrb(o: Orb) {
    if (!o.group) return;
    this.engine.scene.remove(o.group);
    o.group.traverse((c) => {
      if (c instanceof THREE.Mesh) {
        c.geometry.dispose();
        (c.material as THREE.Material).dispose();
      }
    });
    o.group = undefined;
    o.mat = undefined;
    o.ringMat = undefined;
  }

  private spawnPickupRing(x: number, z: number, color: number) {
    const mat = this.pickupRingMat.clone();
    mat.color.setHex(color);
    mat.opacity = 0.9;
    const mesh = new THREE.Mesh(this.pickupRingGeo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(x, -0.35, z);
    this.engine.scene.add(mesh);
    this.pickupRings.push({ mesh, born: performance.now() });
  }

  private frame(dt: number, _t: number) {
    if (this.gameOver) return;
    const now = performance.now();
    const runMs = now - this.startedAt;
    if (runMs > RUN_MS) { this.finish(); return; }

    // v6 — engagement signal drives the post-processing boost + adaptive
    // drone. chain/5 is the natural ramp: it climbs as the player nails
    // same-color pickups and drops to 0 on a predator hit, so both the
    // bloom and the audio bed pulse with momentum. Per memory
    // feedback-sibling-games-shallow §"escalation hook".
    const engagement = Math.min(1, this.chain / 5);
    this.engine.setBoost(engagement, dt);
    try { this.audio.setDroneIntensity(0.15 + engagement * 0.55); } catch { /* off */ }

    // Biome cycle every 30s.
    const targetBiome = Math.floor(runMs / 30_000) % PALETTES.length;
    if (targetBiome !== this.targetBiome) {
      this.currentBiome = this.targetBiome;
      this.targetBiome = targetBiome;
      this.biomeBlend = 0;
    }
    if (this.currentBiome !== this.targetBiome) {
      this.biomeBlend = Math.min(1, this.biomeBlend + dt * 0.4);
      const a = PALETTES[this.currentBiome];
      const b = PALETTES[this.targetBiome];
      this.floorMat.color.copy(new THREE.Color(a.floor).lerp(new THREE.Color(b.floor), this.biomeBlend));
      this.rimMat.color.copy(new THREE.Color(a.rim).lerp(new THREE.Color(b.rim), this.biomeBlend));
      if (this.biomeBlend >= 1) this.currentBiome = this.targetBiome;
    }
    // v5(b): compose final fog color = base biome (or mid-blend) tinted
    // toward the active brew color. Tint linearly fades to 0 over ~2 s.
    if (this.engine.scene.fog instanceof THREE.Fog) {
      const a = PALETTES[this.currentBiome];
      const b = PALETTES[this.targetBiome];
      this.brewFogTmp.setHex(a.fog).lerp(new THREE.Color(b.fog), this.biomeBlend);
      const remaining = this.brewFogTintUntil - runMs;
      if (remaining > 0) {
        const strength = Math.min(1, remaining / 2000) * 0.55;
        this.brewFogTmp.lerp(this.brewFogColor, strength);
      }
      this.engine.scene.fog.color.copy(this.brewFogTmp);
    }

    // Ship motion (game plane is XZ now; mapping ARENA_HALF stays the same).
    if (this.pointerActive) {
      const dx = this.pointerPos.x - this.shipPos.x;
      const dz = this.pointerPos.y - this.shipPos.y;
      const d = Math.hypot(dx, dz);
      const move = Math.min(d, MAX_SHIP_SPEED * dt);
      if (d > 0.001) {
        this.shipPos.x += (dx / d) * move;
        this.shipPos.y += (dz / d) * move;
      }
    }
    this.shipPos.x = Math.max(-ARENA_HALF, Math.min(ARENA_HALF, this.shipPos.x));
    this.shipPos.y = Math.max(-ARENA_HALF, Math.min(ARENA_HALF, this.shipPos.y));
    this.ship.position.set(this.shipPos.x, 0.4, this.shipPos.y);
    // Tilt ship slightly along velocity.
    if (this.pointerActive) {
      const dx = this.pointerPos.x - this.shipPos.x;
      const dz = this.pointerPos.y - this.shipPos.y;
      const heading = Math.atan2(dx, dz);
      this.ship.rotation.y = heading;
    }
    // Chain-color halo intensity.
    const chainGlow = this.chain >= 5 ? 0xffd54a : this.chain >= 3 ? 0xff3df0 : 0x66ddff;
    this.shipHaloMat.color.setHex(chainGlow);
    this.shipHaloMat.opacity = 0.4 + this.chain * 0.08;

    // Spawn / decay / collect orbs.
    for (const o of this.orbs) {
      if (o.picked) continue;
      if (!o.spawned && runMs >= o.spawn_t_ms) this.spawnOrb(o);
      if (!o.spawned) continue;
      const age = runMs - o.spawn_t_ms;
      if (age > ORB_LIFE_MS) {
        o.picked = true;
        this.despawnOrb(o);
        continue;
      }
      // v5(a): predators drift along a deterministic dir over their
      // life. Compute the current position once per frame; reuse for
      // both the mesh position and the pickup-overlap test.
      const pos = predatorPosAt(o, runMs);
      // Decay visual: emissive + scale ramps down. v5(c): reduced-motion
      // users get a steeper fade arc — emissive, opacity, and scale all
      // ramp closer to 0 by end of life, and the brightness curve is
      // squared so the loss of value is visible from spawn rather than
      // becoming legible only in the last half-second.
      if (o.group && o.mat && o.ringMat) {
        const v = 1 - age / ORB_LIFE_MS;
        if (this.reducedMotion) {
          const v2 = v * v; // accelerating fade — orb visibly loses brightness early
          o.mat.emissiveIntensity = 0.15 + 1.35 * v2;
          o.ringMat.opacity = 0.05 + 0.55 * v2;
        } else {
          o.mat.emissiveIntensity = 0.5 + 1.0 * v;
          o.ringMat.opacity = 0.25 + 0.35 * v;
        }
        // Bob up and down — life-feel.
        const bob = Math.sin(now * 0.004 + o.idx * 0.4) * 0.18;
        // Predators visually slide along their drift vector.
        if (o.color === 3) o.group.position.set(pos.x, 0.6 + bob, pos.y);
        else o.group.position.y = 0.6 + bob;
        const scaleBase = this.reducedMotion ? 0.3 : 0.65;
        const scaleRange = this.reducedMotion ? 0.7 : 0.35;
        o.group.scale.setScalar(scaleBase + scaleRange * v);
        // Rotate body for sparkle.
        const body = o.group.children[0] as THREE.Mesh;
        body.rotation.y = now * 0.001 + o.idx;
        body.rotation.x = now * 0.0008;
      }
      // Pickup check — radius widens during an active brew. Predators
      // are tested against their drifted position (matches the server).
      const ddx = pos.x - this.shipPos.x;
      const ddz = pos.y - this.shipPos.y;
      const magnetActive = runMs < this.magnetUntil;
      const radius = magnetActive ? MAGNET_RADIUS : PICKUP_RADIUS;
      if (Math.hypot(ddx, ddz) <= radius) {
        this.pickup(o, runMs);
      }
    }

    // Update pickup rings (expand + fade).
    for (let i = this.pickupRings.length - 1; i >= 0; i--) {
      const r = this.pickupRings[i];
      const age = (now - r.born) / 1000;
      const t = Math.min(1, age / 0.7);
      r.mesh.scale.setScalar(1 + t * 4);
      (r.mesh.material as THREE.MeshBasicMaterial).opacity = (1 - t) * 0.8;
      if (t >= 1) {
        this.engine.scene.remove(r.mesh);
        (r.mesh.material as THREE.Material).dispose();
        this.pickupRings.splice(i, 1);
      }
    }

    // Brew shockwave decay (separate from per-pickup rings).
    if (this.brewFx) {
      const age = (now - this.brewFxBornMs) / 1000;
      const t = Math.min(1, age / 1.2);
      this.brewFx.scale.setScalar(1 + t * 10);
      (this.brewFx.material as THREE.MeshBasicMaterial).opacity = (1 - t) * 0.6;
      if (t >= 1) {
        this.engine.scene.remove(this.brewFx);
        this.brewFx.geometry.dispose();
        (this.brewFx.material as THREE.Material).dispose();
        this.brewFx = null;
      }
    }

    const pickupCount = this.pickups.length;
    const magnetTimeLeftMs = Math.max(0, this.magnetUntil - runMs);
    this.cb.onHud({
      runMs,
      pickups: pickupCount,
      scoreEstimate: this.scoreEstimate,
      chain: this.chain,
      brews: this.brews,
      magnetTimeLeftMs,
    });
  }

  private triggerBrewFx(color: OrbColor, runMs: number) {
    // Large expanding shockwave centered on the ship + audio arpeggio.
    if (this.brewFx) {
      this.engine.scene.remove(this.brewFx);
      this.brewFx.geometry.dispose();
      (this.brewFx.material as THREE.Material).dispose();
    }
    const geo = new THREE.RingGeometry(1.0, 1.4, 64);
    const mat = new THREE.MeshBasicMaterial({
      color: COLOR_HEX[color],
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    this.brewFx = new THREE.Mesh(geo, mat);
    this.brewFx.rotation.x = -Math.PI / 2;
    this.brewFx.position.set(this.shipPos.x, -0.3, this.shipPos.y);
    this.engine.scene.add(this.brewFx);
    this.brewFxBornMs = performance.now();
    // v5(b): tint the shared fog channel toward the brew color for ~2 s.
    this.brewFogColor.setHex(COLOR_HEX[color]);
    this.brewFogTintUntil = runMs + 2000;
    // Arpeggio per color.
    const detunes = color === 0 ? [0, 7, 12] : color === 1 ? [-5, 2, 9] : [0, 4, 7];
    this.audio.chime(detunes[0], -0.4);
    this.audio.chime(detunes[1], 0);
    this.audio.chime(detunes[2], 0.4);
    void runMs;
  }

  private pickup(o: Orb, runMs: number) {
    o.picked = true;
    // v5(a): for predators the visible position has drifted from spawn;
    // draw the pickup burst where the player actually caught it.
    const pos = predatorPosAt(o, runMs);
    // v4: predator pickup — penalty, chain breaks, dissonant cue.
    if (o.color === 3) {
      this.scoreEstimate = Math.max(0, this.scoreEstimate - 250);
      this.chain = 0;
      this.lastColor = -1;
      this.pickups.push({ t: Math.round(runMs), orb_idx: o.idx });
      this.audio.thud();
      this.audio.chime(-5, 0.2);
      this.spawnPickupRing(pos.x, pos.y, COLOR_HEX[3]);
      this.despawnOrb(o);
      return;
    }
    const age = runMs - o.spawn_t_ms;
    const value = Math.round(100 * Math.max(0, 1 - age / ORB_LIFE_MS));
    const prevChain = this.chain;
    if (this.lastColor === -1 || o.color === this.lastColor) this.chain = Math.min(5, this.chain + 1);
    else this.chain = 1;
    if (this.chain > this.bestChain) this.bestChain = this.chain;
    this.lastColor = o.color;
    const mult = 1 + this.chain * 0.2;
    const earned = Math.round(value * mult);
    this.scoreEstimate += earned;
    this.pickups.push({ t: Math.round(runMs), orb_idx: o.idx });
    const detune = this.chain >= 5 ? 12 : this.chain >= 4 ? 7 : this.chain >= 3 ? 4 : 0;
    this.audio.chime(detune, 0);
    this.spawnPickupRing(o.x, o.y, COLOR_HEX[o.color]);

    // v3 brew + v4 per-color bonus.
    if (this.chain === 3 && prevChain === 2) {
      this.brews += 1;
      // Per-color bonus (cyan=500, magenta=700, gold=1000) layered on top of the base brew bonus.
      const colorBonus = o.color === 0 ? 500 : o.color === 1 ? 700 : 1000;
      this.scoreEstimate += BREW_BONUS_CLIENT + colorBonus;
      this.magnetUntil = runMs + MAGNET_DURATION_MS;
      this.triggerBrewFx(o.color as 0 | 1 | 2, runMs);
    }
    this.despawnOrb(o);
  }

  private finish() {
    this.gameOver = true;
    this.audio.setDroneIntensity(0);
    this.cb.onGameOver({
      pickups: this.pickups,
      scoreEstimate: this.scoreEstimate,
      bestChain: this.bestChain,
      pickupCount: this.pickups.length,
      brews: this.brews,
    });
  }

  setMuted(b: boolean) { this.audio.setMuted(b); }
  setVolume(v: number) { this.audio.setVolume(v); }
  armAudio() { this.audio.arm(); }

  dispose() {
    // v6 — quiet the drone so a navigate-away → re-mount doesn't stack
    // beds. Same pattern as Phalanx 2bc3ebc / Splinter eab2d80.
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.canvas.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    for (const o of this.orbs) this.despawnOrb(o);
    for (const r of this.pickupRings) {
      this.engine.scene.remove(r.mesh);
      (r.mesh.material as THREE.Material).dispose();
    }
    this.pickupRings = [];
    this.pickupRingGeo.dispose();
    // v6 — drop the parallax starfield layers.
    for (const layer of this.starLayers) {
      this.engine.scene.remove(layer);
      layer.geometry.dispose();
      (layer.material as THREE.Material).dispose();
    }
    this.starLayers = [];
    this.engine.dispose();
  }
}
